import React, { useState } from "react";
import axios from "axios";
import {
  FileText, Plus, X, Clock, User, CheckCircle,
  AlertTriangle, ChevronDown, Send
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  critical: "border-red-300 bg-red-50 text-red-800",
  high: "border-amber-300 bg-amber-50 text-amber-800",
  medium: "border-blue-200 bg-blue-50 text-blue-800",
  low: "border-[#e8e0d0] bg-[#faf7f2] text-[#555]",
};

const STATUS_BADGE = {
  open: "bg-red-100 text-red-700",
  in_progress: "bg-amber-100 text-amber-700",
  resolved: "bg-emerald-100 text-emerald-700",
  closed: "bg-gray-100 text-gray-600",
};

export default function Deal360Requests({ requests = [], leadId, isInternal = false, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", assigned_to: "", due_date: "" });
  const [creating, setCreating] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const openReqs = requests.filter(r => r.status === "open" || r.status === "in_progress");
  const closedReqs = requests.filter(r => r.status === "resolved" || r.status === "closed");

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.due_date) delete payload.due_date;
      await axios.post(`${API}/entity/deal/${leadId}/requests`, payload, { headers });
      toast.success("Request created");
      setShowCreate(false);
      setForm({ title: "", description: "", priority: "medium", assigned_to: "", due_date: "" });
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create request");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="requests-module">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] flex items-center gap-1.5">
          <FileText className="h-3 w-3" /> Requests & Tasks
          {openReqs.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-600">{openReqs.length} open</span>
          )}
        </h3>
        {isInternal && (
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
            data-testid="create-request-btn"
          >
            {showCreate ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
            {showCreate ? "Cancel" : "New Request"}
          </button>
        )}
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/30 p-4 space-y-3" data-testid="create-request-form">
          <div>
            <label className="text-[9px] text-[#999] uppercase font-bold block mb-1">Title</label>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g., Upload insurance binder, Verify liquidity..."
              className="w-full text-xs border border-[#ddd] rounded-lg px-2.5 py-1.5 bg-white focus:ring-1 focus:ring-blue-400 focus:outline-none" data-testid="req-title-input" />
          </div>
          <div>
            <label className="text-[9px] text-[#999] uppercase font-bold block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Additional context..." rows={2}
              className="w-full text-xs border border-[#ddd] rounded-lg px-2.5 py-1.5 bg-white focus:ring-1 focus:ring-blue-400 focus:outline-none resize-none" data-testid="req-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] text-[#999] uppercase font-bold block mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full text-xs border border-[#ddd] rounded-lg px-2.5 py-1.5 bg-white focus:ring-1 focus:ring-blue-400 focus:outline-none" data-testid="req-priority-select">
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="text-[9px] text-[#999] uppercase font-bold block mb-1">Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                className="w-full text-xs border border-[#ddd] rounded-lg px-2.5 py-1.5 bg-white focus:ring-1 focus:ring-blue-400 focus:outline-none" data-testid="req-due-input" />
            </div>
          </div>
          <button onClick={handleCreate} disabled={creating}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 transition-colors" data-testid="submit-request-btn">
            <Send className="h-3 w-3" /> {creating ? "Creating..." : "Create Request"}
          </button>
        </div>
      )}

      {/* Open Requests */}
      {openReqs.length === 0 && !showCreate && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center text-xs text-emerald-700 flex items-center justify-center gap-2" data-testid="no-requests">
          <CheckCircle className="h-4 w-4" /> No open requests
        </div>
      )}

      <div className="space-y-2">
        {openReqs.map(req => (
          <div key={req.request_id} className={`rounded-xl border p-4 ${PRIORITY_STYLES[req.priority] || PRIORITY_STYLES.medium}`} data-testid={`request-${req.request_id}`}>
            <div className="flex items-start justify-between mb-1.5">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold">{req.title}</div>
                {req.description && <p className="text-[10px] opacity-80 mt-0.5 line-clamp-2">{req.description}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${STATUS_BADGE[req.status]}`}>{req.status}</span>
                <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase opacity-70">{req.priority}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[9px] opacity-60 mt-2">
              {req.created_at && <span><Clock className="h-2.5 w-2.5 inline mr-0.5" />{new Date(req.created_at).toLocaleDateString()}</span>}
              {req.due_date && <span className="font-bold text-red-600">Due: {new Date(req.due_date).toLocaleDateString()}</span>}
              {req.assigned_to && <span><User className="h-2.5 w-2.5 inline mr-0.5" />{req.assigned_to.slice(0, 8)}</span>}
              {req.created_by && <span>Created by: {req.created_by.slice(0, 8)}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Closed Requests */}
      {closedReqs.length > 0 && (
        <details className="group">
          <summary className="text-[10px] text-[#999] cursor-pointer hover:text-[#555] flex items-center gap-1 mb-2">
            <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" /> {closedReqs.length} resolved request{closedReqs.length !== 1 ? "s" : ""}
          </summary>
          <div className="space-y-1.5 pl-2">
            {closedReqs.map(req => (
              <div key={req.request_id} className="rounded-lg border border-[#e8e0d0] bg-[#faf7f2] p-2.5 opacity-70">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium text-[#555]">{req.title}</span>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${STATUS_BADGE[req.status]}`}>{req.status}</span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
